import { motion } from 'framer-motion';
import {
  MapPin,
  Mail,
  CalendarDays,
  Facebook,
  Instagram,
  MessageCircle,
} from 'lucide-react';
import { SOCIALS } from '../socialLinks';
import { useLang } from '../i18n';

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  whatsapp: MessageCircle,
};

const card =
  'relative rounded-2xl border border-[#d4af37]/15 bg-[#0c171c]/70 backdrop-blur-xl p-8 sm:p-10 overflow-hidden group hover:border-[#d4af37]/40 hover:-translate-y-1 transition-all duration-500';

const glow =
  'absolute -top-24 -right-24 w-56 h-56 rounded-full bg-[#d4af37]/10 blur-3xl group-hover:bg-[#d4af37]/20 transition-colors duration-700 pointer-events-none';

const InfoSection = () => {
  const { t } = useLang();
  return (
    <section
      id="sedi"
      data-testid="info-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="font-mono-alt text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#e6c687] mb-4">
            {t('info.overline')}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f3e5ab] tracking-tight">
            {t('info.title.a')}{' '}
            <span className="italic text-[#d4af37]">Masterclass</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: MapPin,
              title: 'Trinity Musical School',
              lines: ['Area Tosi — Via Luigi Einaudi, 16', 'Rovigo'],
              id: 'venue-rovigo-card',
            },
            {
              icon: MapPin,
              title: 'Salotto Musicale "R. Guiso"',
              lines: ['Piazza Matteotti, 112', 'Polesella (RO)'],
              id: 'venue-polesella-card',
            },
            {
              icon: CalendarDays,
              title: t('info.dates'),
              lines: [t('info.dates.l1'), t('info.dates.l2')],
              id: 'dates-card',
            },
          ].map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={c.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={card}
            >
              <div className={glow} />
              <c.icon size={22} strokeWidth={1.4} className="text-[#d4af37] mb-6" />
              <h3 className="font-display text-2xl text-[#f3e5ab] mb-3">{c.title}</h3>
              {c.lines.map((l) => (
                <p key={l} className="text-sm text-[#a3b1b6] leading-relaxed">
                  {l}
                </p>
              ))}
            </motion.div>
          ))}
        </div>

        <motion.div
          id="contatti"
          data-testid="contact-cta"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 relative rounded-2xl border border-[#d4af37]/25 bg-[#0a1216] p-10 sm:p-14 overflow-hidden"
        >
          <div className={glow} />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
            <div>
              <h3 className="font-display text-3xl sm:text-4xl text-[#f3e5ab] tracking-tight">
                {t('cta.title.a')}{' '}
                <span className="italic text-[#d4af37]">{t('cta.title.b')}</span>
              </h3>
              <p className="mt-3 text-sm text-[#a3b1b6]">{t('cta.sub')}</p>
              <div className="mt-6 flex items-center gap-3">
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICONS[s.id];
                  return (
                    <a
                      key={s.id}
                      data-testid={`social-${s.id}-link`}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.label}
                      className="w-11 h-11 rounded-full border border-[#d4af37]/35 flex items-center justify-center text-[#e6c687] transition-all duration-300 hover:bg-[#d4af37]/15 hover:border-[#d4af37]/60 hover:-translate-y-0.5"
                    >
                      <Icon size={17} strokeWidth={1.6} />
                    </a>
                  );
                })}
              </div>
            </div>
            <a
              data-testid="contact-email-btn"
              href="mailto:info@trinitymusicalschool.net"
              className="shrink-0 inline-flex items-center gap-3 rounded-full bg-[#d4af37] px-8 py-4 font-mono-alt text-[11px] tracking-[0.2em] uppercase text-[#070e12] font-medium transition-all duration-300 hover:bg-[#f3e5ab] hover:shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)]"
            >
              <Mail size={16} strokeWidth={1.8} />
              {t('cta.button')}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InfoSection;
